<template>
  <div class="instrument-library p-4 text-xs">
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-baseline gap-2">
        <h3 class="font-semibold text-gray-900">Survey Instruments</h3>
        <span class="text-gray-400">{{ filteredInstruments.length }} of {{ instruments.length }}</span>
      </div>
      <button
        @click="openCreateModal"
        class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 font-medium"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        New Instrument
      </button>
    </div>

    <!-- Search -->
    <div class="relative mb-4 max-w-md">
      <font-awesome-icon :icon="['fas', 'search']" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search name, description, or category..."
        class="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-dark focus:border-transparent"
      />
    </div>

    <!-- Master / child layout -->
    <div class="flex gap-4 items-start">
      <!-- MASTER: instrument table -->
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden flex-1 min-w-0">
        <div class="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
          <table class="w-full text-xs border-collapse">
            <thead class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  v-for="col in columns"
                  :key="col.key"
                  scope="col"
                  class="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap select-none"
                  :class="[col.align === 'right' ? 'text-right' : '', col.sortable ? 'cursor-pointer hover:text-gray-900' : '']"
                  @click="col.sortable && toggleSort(col.key)"
                >
                  <span class="inline-flex items-center gap-1" :class="col.align === 'right' ? 'flex-row-reverse' : ''">
                    {{ col.label }}
                    <font-awesome-icon
                      v-if="col.sortable && sortKey === col.key"
                      :icon="['fas', sortDir === 'asc' ? 'arrow-up' : 'arrow-down']"
                      class="text-[10px] text-gray-400"
                    />
                  </span>
                </th>
                <th scope="col" class="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide w-px whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>

            <!-- Loading skeleton -->
            <tbody v-if="loading">
              <tr v-for="n in 8" :key="`sk-${n}`" class="border-b border-gray-100">
                <td v-for="col in columns" :key="col.key" class="px-4 py-3">
                  <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: col.key === 'name' ? '70%' : col.key === 'description' ? '90%' : '40%' }"></div>
                </td>
                <td class="px-4 py-3"><div class="h-3 w-12 bg-gray-100 rounded animate-pulse ml-auto"></div></td>
              </tr>
            </tbody>

            <!-- Data -->
            <tbody v-else>
              <tr
                v-for="instrument in sortedInstruments"
                :key="instrument.id"
                class="group border-b border-gray-100 hover:bg-gray-50 transition-colors align-top cursor-pointer"
                :class="selectedId === instrument.id ? 'bg-blue-50/60' : ''"
                @click="selectInstrument(instrument)"
              >
                <td class="px-4 py-3 font-medium text-gray-900 min-w-[180px]">
                  {{ instrument.name }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span
                    v-if="instrument.is_global"
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 text-xs font-medium rounded"
                    title="Kairon-owned shared reference instrument (read-only)"
                  >
                    <font-awesome-icon :icon="['fas', 'lock']" class="text-[10px]" />
                    Global
                  </span>
                  <span
                    v-else
                    class="inline-flex px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded"
                    title="Tenant-specific instrument (editable)"
                  >
                    Tenant
                  </span>
                </td>
                <td class="px-4 py-3 text-gray-600 min-w-[240px] max-w-[420px]">
                  <span v-if="instrument.description">{{ instrument.description }}</span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span
                    v-if="instrument.metadata?.category"
                    class="inline-flex px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded"
                  >
                    {{ instrument.metadata.category }}
                  </span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-4 py-3 text-right tabular-nums text-gray-900 whitespace-nowrap">
                  {{ instrument.question_count }}
                </td>
                <td class="px-4 py-3 text-gray-500 whitespace-nowrap">
                  {{ formatDate(instrument.updated_at) }}
                </td>
                <td class="px-4 py-3 text-right whitespace-nowrap" @click.stop>
                  <div class="inline-flex items-center gap-1">
                    <button
                      v-if="!instrument.is_global"
                      @click="editInstrument(instrument)"
                      class="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                      title="Edit instrument"
                    >
                      <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                    </button>
                    <button
                      v-if="!instrument.is_global"
                      @click="deleteInstrument(instrument)"
                      class="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                      title="Delete instrument"
                    >
                      <font-awesome-icon :icon="['fas', 'trash-can']" />
                    </button>
                    <span v-else class="text-gray-300 text-[10px] italic pr-1">read-only</span>
                  </div>
                </td>
              </tr>

              <!-- Empty state -->
              <tr v-if="sortedInstruments.length === 0">
                <td :colspan="columns.length + 1" class="px-4 py-16 text-center">
                  <font-awesome-icon :icon="['fas', 'clipboard-list']" class="text-gray-300 text-2xl mb-2" />
                  <p class="text-gray-600">{{ searchQuery ? 'No instruments match your search.' : 'No instruments yet.' }}</p>
                  <button v-if="!searchQuery" @click="openCreateModal" class="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Create the first instrument
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- CHILD: question panel -->
      <div
        v-if="selectedInstrument"
        class="bg-white border border-gray-200 rounded-lg w-[420px] shrink-0 max-h-[calc(100vh-300px)] flex flex-col"
      >
        <!-- panel header -->
        <div class="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <h4 class="font-semibold text-gray-900 truncate">{{ selectedInstrument.name }}</h4>
              <span
                v-if="selectedInstrument.is_global"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-50 text-violet-700 text-[10px] font-medium rounded shrink-0"
              >
                <font-awesome-icon :icon="['fas', 'lock']" class="text-[9px]" /> Global
              </span>
            </div>
            <p class="text-gray-400 mt-0.5">{{ panelQuestions.length }} questions</p>
          </div>
          <button @click="closePanel" class="text-gray-400 hover:text-gray-600 shrink-0" title="Close">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- read-only notice for global -->
        <div v-if="selectedInstrument.is_global" class="px-4 py-2 bg-violet-50/60 text-violet-700 text-[11px] border-b border-violet-100">
          Kairon reference instrument — questions are read-only.
        </div>

        <!-- questions list -->
        <div class="flex-1 overflow-y-auto px-2 py-2">
          <div v-if="panelLoading" class="flex items-center justify-center py-10 text-gray-400">
            <font-awesome-icon :icon="['fas', 'spinner']" class="animate-spin mr-2" /> Loading…
          </div>

          <div v-else-if="panelQuestions.length === 0" class="text-center py-10 text-gray-400">
            No questions in this instrument.
          </div>

          <ol v-else class="space-y-1">
            <li
              v-for="(q, idx) in panelQuestions"
              :key="q.question_id"
              class="group/q flex items-start gap-2 px-2 py-2 rounded hover:bg-gray-50"
            >
              <span class="text-gray-400 tabular-nums w-5 shrink-0 text-right">{{ idx + 1 }}.</span>
              <div class="min-w-0 flex-1">
                <p class="text-gray-900">{{ q.intent }}</p>
                <p v-if="q.sample_question_text" class="text-gray-500 mt-0.5">{{ q.sample_question_text }}</p>
              </div>
              <div v-if="!selectedInstrument.is_global" class="flex items-center gap-1 opacity-0 group-hover/q:opacity-100 transition-opacity shrink-0">
                <button @click="editQuestion(q)" class="p-1 text-gray-400 hover:text-blue-600" title="Edit question">
                  <font-awesome-icon :icon="['fas', 'pen-to-square']" class="text-[11px]" />
                </button>
                <button @click="removeQuestion(q)" class="p-1 text-gray-400 hover:text-red-600" title="Remove from instrument">
                  <font-awesome-icon :icon="['fas', 'trash-can']" class="text-[11px]" />
                </button>
              </div>
            </li>
          </ol>
        </div>

        <!-- add question (tenant only) -->
        <div v-if="!selectedInstrument.is_global" class="border-t border-gray-200 p-3">
          <div v-if="!adding" class="text-right">
            <button @click="startAdd" class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium">
              <font-awesome-icon :icon="['fas', 'plus']" /> Add question
            </button>
          </div>
          <div v-else class="space-y-2">
            <input
              v-model="draft.intent"
              placeholder="Question intent (what it captures)"
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            />
            <textarea
              v-model="draft.sample_question_text"
              placeholder="Sample question text (how to phrase it)"
              rows="2"
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            ></textarea>
            <div class="flex items-center justify-end gap-2">
              <button @click="cancelAdd" class="px-2 py-1 text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                @click="saveQuestion"
                :disabled="!draft.intent || saving"
                class="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {{ saving ? 'Saving…' : (draft.question_id ? 'Save' : 'Add') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Instrument Editor Modal -->
    <InstrumentEditor
      v-if="showEditor"
      :instrument="editingInstrument"
      @save="saveInstrument"
      @close="closeEditor"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import InstrumentEditor from './InstrumentEditor.vue';

export default {
  name: 'InstrumentLibrary',

  components: {
    FontAwesomeIcon,
    InstrumentEditor
  },

  data() {
    return {
      instruments: [],
      loading: true,
      searchQuery: '',
      sortKey: 'name',
      sortDir: 'asc',
      showEditor: false,
      editingInstrument: null,
      // child panel
      selectedId: null,
      panelQuestions: [],
      panelLoading: false,
      adding: false,
      saving: false,
      draft: { question_id: null, intent: '', sample_question_text: '' },
      columns: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'scope', label: 'Scope', sortable: true },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'category', label: 'Category', sortable: true },
        { key: 'question_count', label: 'Questions', sortable: true, align: 'right' },
        { key: 'updated_at', label: 'Updated', sortable: true }
      ]
    };
  },

  computed: {
    filteredInstruments() {
      if (!this.searchQuery) return this.instruments;
      const q = this.searchQuery.toLowerCase();
      return this.instruments.filter(inst =>
        inst.name?.toLowerCase().includes(q) ||
        inst.description?.toLowerCase().includes(q) ||
        inst.metadata?.category?.toLowerCase().includes(q)
      );
    },

    sortedInstruments() {
      const rows = [...this.filteredInstruments];
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const key = this.sortKey;
      return rows.sort((a, b) => {
        let av = this.sortValue(a, key);
        let bv = this.sortValue(b, key);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'string') return av.localeCompare(bv) * dir;
        return (av - bv) * dir;
      });
    },

    selectedInstrument() {
      return this.instruments.find(i => i.id === this.selectedId) || null;
    }
  },

  mounted() {
    this.fetchInstruments();
  },

  methods: {
    sortValue(inst, key) {
      switch (key) {
        case 'name': return (inst.name || '').toLowerCase();
        case 'scope': return inst.is_global ? 'global' : 'tenant';
        case 'category': return inst.metadata?.category?.toLowerCase() ?? null;
        case 'question_count': return inst.question_count ?? 0;
        case 'updated_at': return inst.updated_at ? new Date(inst.updated_at).getTime() : null;
        default: return null;
      }
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },

    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },

    async fetchInstruments() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/api/survey-instruments/');
        this.instruments = response.data;
      } catch (error) {
        console.error('Error fetching instruments:', error);
        this.$emit('show-message', 'Failed to load instruments', 'error');
      } finally {
        this.loading = false;
      }
    },

    // ---- child panel ----
    async selectInstrument(instrument) {
      this.selectedId = instrument.id;
      this.cancelAdd();
      await this.fetchQuestions();
    },

    closePanel() {
      this.selectedId = null;
      this.panelQuestions = [];
      this.cancelAdd();
    },

    async fetchQuestions() {
      if (!this.selectedId) return;
      this.panelLoading = true;
      try {
        const response = await this.$axios.get(`/api/survey-instruments/${this.selectedId}/questions/`);
        this.panelQuestions = response.data.questions || [];
      } catch (error) {
        console.error('Error fetching instrument questions:', error);
        this.$emit('show-message', 'Failed to load questions', 'error');
        this.panelQuestions = [];
      } finally {
        this.panelLoading = false;
      }
    },

    startAdd() {
      this.draft = { question_id: null, intent: '', sample_question_text: '' };
      this.adding = true;
    },

    cancelAdd() {
      this.adding = false;
      this.draft = { question_id: null, intent: '', sample_question_text: '' };
    },

    editQuestion(q) {
      this.draft = {
        question_id: q.question_id,
        intent: q.intent,
        sample_question_text: q.sample_question_text || ''
      };
      this.adding = true;
    },

    async saveQuestion() {
      if (!this.draft.intent) return;
      this.saving = true;
      try {
        if (this.draft.question_id) {
          // edit existing question (tenant-scoped; backend rejects global)
          await this.$axios.patch(`/api/questions/${this.draft.question_id}/`, {
            intent: this.draft.intent,
            sample_question_text: this.draft.sample_question_text
          });
          this.$emit('show-message', 'Question updated', 'success');
        } else {
          // create + link in one call
          await this.$axios.post('/api/questions/add-to-instrument/', {
            survey_instrument: this.selectedId,
            intent: this.draft.intent,
            sample_question_text: this.draft.sample_question_text
          });
          this.$emit('show-message', 'Question added', 'success');
        }
        this.cancelAdd();
        await this.fetchQuestions();
        await this.fetchInstruments();
      } catch (error) {
        const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to save question';
        console.error('Error saving question:', error);
        this.$emit('show-message', msg, 'error');
      } finally {
        this.saving = false;
      }
    },

    async removeQuestion(q) {
      if (!confirm(`Remove "${q.intent}" from this instrument?`)) return;
      try {
        // find the link row for this instrument + question, then delete it
        const links = await this.$axios.get('/api/survey-instrument-questions/', {
          params: { survey_instrument: this.selectedId, question: q.question_id }
        });
        const link = (links.data || [])[0];
        if (link) {
          await this.$axios.delete(`/api/survey-instrument-questions/${link.id}/`);
        }
        this.$emit('show-message', 'Question removed', 'success');
        await this.fetchQuestions();
        await this.fetchInstruments();
      } catch (error) {
        const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to remove question';
        console.error('Error removing question:', error);
        this.$emit('show-message', msg, 'error');
      }
    },

    // ---- instrument CRUD ----
    openCreateModal() {
      this.editingInstrument = null;
      this.showEditor = true;
    },

    editInstrument(instrument) {
      this.editingInstrument = { ...instrument };
      this.showEditor = true;
    },

    async saveInstrument(instrumentData) {
      try {
        if (instrumentData.id) {
          await this.$axios.patch(`/api/survey-instruments/${instrumentData.id}/`, instrumentData);
          this.$emit('show-message', 'Instrument updated successfully', 'success');
        } else {
          await this.$axios.post('/api/survey-instruments/', instrumentData);
          this.$emit('show-message', 'Instrument created successfully', 'success');
        }
        this.closeEditor();
        this.fetchInstruments();
        this.$emit('refresh');
      } catch (error) {
        const msg = error.response?.data?.detail || 'Failed to save instrument';
        console.error('Error saving instrument:', error);
        this.$emit('show-message', msg, 'error');
      }
    },

    async deleteInstrument(instrument) {
      if (!confirm(`Delete instrument "${instrument.name}"?`)) return;
      try {
        await this.$axios.delete(`/api/survey-instruments/${instrument.id}/`);
        this.$emit('show-message', 'Instrument deleted successfully', 'success');
        if (this.selectedId === instrument.id) this.closePanel();
        this.fetchInstruments();
        this.$emit('refresh');
      } catch (error) {
        const msg = error.response?.data?.detail || 'Failed to delete instrument';
        console.error('Error deleting instrument:', error);
        this.$emit('show-message', msg, 'error');
      }
    },

    closeEditor() {
      this.showEditor = false;
      this.editingInstrument = null;
    }
  }
};
</script>

<style scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
